import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getAuthToken } from "./api";
import useReportDownload from "./hooks/useReportDownload";
import StudentInsights from "./components/StudentInsights";
import "./Dashboard.css";

/**
 * Interviewee Dashboard
 * Shows personal improvement metrics and score trends over time
 */

function Dashboard({ user, onStartInterview, reportLocked = false, onLockedReportClick }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviewPage, setInterviewPage] = useState(0);
  const INTERVIEW_PAGE_SIZE = 5;
  const apiBase = process.env.REACT_APP_API_BASE || "http://localhost:8000";
  const { downloadingReport, handleDownloadReport } = useReportDownload(apiBase, user);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = await getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${apiBase}/dashboard/interviewee`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      const data = await response.json();
      setDashboard(data);
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <h3>Unable to Load Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboard} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="dashboard-container">
        <div className="no-data">
          <p>No dashboard data available</p>
        </div>
      </div>
    );
  }

  const {
    entitlement_status,
    subscription_type = "NONE",
    days_remaining = null,
    total_interviews,
    coding_practice_attended = 0,
    test_attempted = 0,
    interview_history = [],
    assessment_history = [],
    coding_history = [],
    hire_readiness,
    improvement_trends,
    strengths_summary,
    weaknesses_summary,
    trial_sessions = []
  } = dashboard;

  // Format plan type to user-friendly display name
  const formatPlanType = (planType) => {
    const planDisplayNames = {
      "SINGLE_INTERVIEW": "1 Interview",
      "FIVE_INTERVIEWS": "5 Interviews",
      "TEN_INTERVIEWS": "10 Interviews",
      "TWENTY_INTERVIEWS": "20 Interviews",
      "MONTHLY_SUBSCRIPTION": "Monthly Plan",
      "NONE": "No Active Plan"
    };
    return planDisplayNames[planType] || planType;
  };

  const formatScore = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Math.round(numeric) : 0;
  };

  // Prepare combined trends for line chart (hire readiness + 3 individual metrics)
  const allDates = new Set();
  
  if (hire_readiness?.trend) {
    hire_readiness.trend.forEach(item => allDates.add(item.date));
  }
  
  if (improvement_trends) {
    ["technical", "communication", "confidence"].forEach(metric => {
      if (improvement_trends[metric]) {
        improvement_trends[metric].forEach(item => allDates.add(item.date));
      }
    });
  }

  const chartData = Array.from(allDates).sort().map(date => {
    const row = { date };
    
    // Add hire readiness
    if (hire_readiness?.trend) {
      const hr = hire_readiness.trend.find(t => t.date === date);
      row.hire_readiness = hr ? hr.score : null;
    }
    
    // Add individual metrics (NO integrity)
    if (improvement_trends?.technical) {
      const tech = improvement_trends.technical.find(t => t.date === date);
      row.technical = tech ? tech.score : null;
    }
    if (improvement_trends?.communication) {
      const comm = improvement_trends.communication.find(t => t.date === date);
      row.communication = comm ? comm.score : null;
    }
    if (improvement_trends?.confidence) {
      const conf = improvement_trends.confidence.find(t => t.date === date);
      row.confidence = conf ? conf.score : null;
    }
    
    return row;
  });

  return (
    <div className="dashboard-container">
      <div className="setup-header setup-header--product" style={{ marginBottom: '2rem', marginLeft: '1.5rem' }}>
        <span className="setup-eyebrow">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
          </svg>
          Dashboard
        </span>
        <h1 className="setup-title">Career Readiness Dashboard</h1>
        <p className="setup-subtitle">Track interview practice, coding performance, and assessment scores seamlessly.</p>
      </div>

      {/* KPI Row */}
      <div className="dashboard-kpi-grid">
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-label">Interviews Attended</div>
          <div className="dashboard-kpi-value">{total_interviews || 0}</div>
          <div className="dashboard-kpi-note">Mock interview attempts</div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-label">Coding Practice Attended</div>
          <div className="dashboard-kpi-value">{coding_practice_attended || 0}</div>
          <div className="dashboard-kpi-note">Code submissions completed</div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-label">Tests Attempted</div>
          <div className="dashboard-kpi-value">{test_attempted || 0}</div>
          <div className="dashboard-kpi-note">Assessment test attempts</div>
        </div>
        <div className="dashboard-kpi-card dashboard-kpi-card--plan">
          <div className="dashboard-kpi-label">Current Plan</div>
          <div className="dashboard-kpi-value dashboard-kpi-value--text">{formatPlanType(subscription_type)}</div>
          <div className="dashboard-kpi-note">
            {days_remaining !== null && days_remaining >= 0 ? `${days_remaining} days remaining` : "Plan status"}
          </div>
        </div>
      </div>

      {/* Score Trend Cards with Individual Charts */}
      {chartData.length > 0 ? (
        <div className="metrics-grid">
          {/* Technical Score Chart */}
          {improvement_trends?.technical && improvement_trends.technical.length > 0 && (
            <div className="metric-card transparent-card">
              <div className="metric-header">
                <div className="metric-info">
                  <h3>Technical Skills</h3>
                  <div className="metric-current-score">
                    {improvement_trends.technical[improvement_trends.technical.length - 1].score}
                    <span className="score-suffix">/100</span>
                  </div>
                  <div className="readiness-badge-spacer" style={{ height: '26px' }}></div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={improvement_trends.technical}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.6)" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip
                    contentStyle={{ 
                      backgroundColor: "rgba(0,0,0,0.9)", 
                      border: "none",
                      borderRadius: "8px",
                      color: "white"
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#8B5FBF"
                    strokeWidth={3}
                    dot={{ fill: "#8B5FBF", r: 4 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Communication Score Chart */}
          {improvement_trends?.communication && improvement_trends.communication.length > 0 && (
            <div className="metric-card transparent-card">
              <div className="metric-header">
                <div className="metric-info">
                  <h3>Communication</h3>
                  <div className="metric-current-score">
                    {improvement_trends.communication[improvement_trends.communication.length - 1].score}
                    <span className="score-suffix">/100</span>
                  </div>
                  <div className="readiness-badge-spacer" style={{ height: '26px' }}></div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={improvement_trends.communication}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.6)" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip
                    contentStyle={{ 
                      backgroundColor: "rgba(0,0,0,0.9)", 
                      border: "none",
                      borderRadius: "8px",
                      color: "white"
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#FF6B6B"
                    strokeWidth={3}
                    dot={{ fill: "#FF6B6B", r: 4 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Confidence Score Chart */}
          {improvement_trends?.confidence && improvement_trends.confidence.length > 0 && (
            <div className="metric-card transparent-card">
              <div className="metric-header">
                <div className="metric-info">
                  <h3>Confidence</h3>
                  <div className="metric-current-score">
                    {improvement_trends.confidence[improvement_trends.confidence.length - 1].score}
                    <span className="score-suffix">/100</span>
                  </div>
                  <div className="readiness-badge-spacer" style={{ height: '26px' }}></div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={improvement_trends.confidence}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.6)" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip
                    contentStyle={{ 
                      backgroundColor: "rgba(0,0,0,0.9)", 
                      border: "none",
                      borderRadius: "8px",
                      color: "white"
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#4ECDC4"
                    strokeWidth={3}
                    dot={{ fill: "#4ECDC4", r: 4 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Hire Readiness Chart */}
          {hire_readiness?.trend && hire_readiness.trend.length > 0 && (
            <div className="metric-card transparent-card hire-readiness-metric">
              <div className="metric-header">
                <div className="metric-info">
                  <h3>Hire Readiness</h3>
                  <div className="metric-current-score">
                    {hire_readiness.score}
                    <span className="score-suffix">/100</span>
                  </div>
                  <div className={`readiness-badge level-${hire_readiness.level.toLowerCase().replace(/_/g, "-")}`}>
                    {hire_readiness.level.replace(/_/g, " ")}
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={hire_readiness.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.6)" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip
                    contentStyle={{ 
                      backgroundColor: "rgba(0,0,0,0.9)", 
                      border: "none",
                      borderRadius: "8px",
                      color: "white"
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#FFD700"
                    strokeWidth={3}
                    dot={{ fill: "#FFD700", r: 4 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <p>Complete your first practice interview to see score trends</p>
        </div>
      )}

      {/* Campus Placement Insights */}
      <StudentInsights user={user} apiBase={apiBase} />

      {/* Historical Interviews */}
      <section className="history-section">
        <div className="history-section-header">
          <div>
            <p className="history-kicker">Interview History</p>
            <h2>Historical interviews attempted</h2>
          </div>
          <span>{interview_history.length} records</span>
        </div>

        {interview_history.length > 0 ? (
          <>
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Interview</th>
                    <th>Date</th>
                    <th>Overall</th>
                    <th>Technical</th>
                    <th>Communication</th>
                    <th>Confidence</th>
                    <th>Report</th>
                  </tr>
                </thead>
                <tbody>
                  {interview_history
                    .slice(interviewPage * INTERVIEW_PAGE_SIZE, (interviewPage + 1) * INTERVIEW_PAGE_SIZE)
                    .map((item) => (
                      <tr key={item.session_id || `${item.role}-${item.created_at}`}>
                        <td>
                          <div className="history-role">{item.role || "Interview"}</div>
                          <div className="history-mode">{item.mode || "Practice"}</div>
                        </td>
                        <td>{item.created_at || "N/A"}</td>
                        <td><span className="history-score">{formatScore(item.scores?.overall)}</span></td>
                        <td>{formatScore(item.scores?.technical)}</td>
                        <td>{formatScore(item.scores?.communication)}</td>
                        <td>{formatScore(item.scores?.confidence)}</td>
                        <td>
                          {reportLocked ? (
                            <button
                              type="button"
                              className="history-report-button"
                              onClick={() => onLockedReportClick && onLockedReportClick()}
                            >
                              🔒 Unlock
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="history-report-button"
                              disabled={downloadingReport || !item.session_id}
                              onClick={() => handleDownloadReport(item.session_id)}
                            >
                              {downloadingReport ? "Downloading..." : "Download"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {interview_history.length > INTERVIEW_PAGE_SIZE && (
              <div className="history-pagination">
                <button
                  type="button"
                  className="history-page-btn"
                  disabled={interviewPage === 0}
                  onClick={() => setInterviewPage((p) => p - 1)}
                >
                  ← Prev
                </button>
                <span className="history-page-info">
                  {interviewPage + 1} / {Math.ceil(interview_history.length / INTERVIEW_PAGE_SIZE)}
                </span>
                <button
                  type="button"
                  className="history-page-btn"
                  disabled={(interviewPage + 1) * INTERVIEW_PAGE_SIZE >= interview_history.length}
                  onClick={() => setInterviewPage((p) => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="history-empty">
            Complete an interview to see your historical attempts and metrics here.
          </div>
        )}
      </section>

      {/* Assessment Test History */}
      <section className="history-section">
        <div className="history-section-header">
          <div>
            <p className="history-kicker" style={{ color: "rgba(102,126,234,0.9)" }}>Assessment History</p>
            <h2>MCQ test scores</h2>
          </div>
          <span>{assessment_history.length} attempts</span>
        </div>

        {assessment_history.length > 0 ? (
          <div className="history-table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Date</th>
                  <th>Difficulty</th>
                  <th>Technical</th>
                  <th>Aptitude</th>
                  <th>Workstyle</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {assessment_history.map((item) => (
                  <tr key={item.attempt_id}>
                    <td>
                      <div className="history-role">{item.role || "Assessment"}</div>
                      {item.company && <div className="history-mode">{item.company}</div>}
                    </td>
                    <td>{item.created_at || "N/A"}</td>
                    <td>
                      <span className="history-mode" style={{ textTransform: "capitalize" }}>
                        {item.difficulty || "—"}
                      </span>
                    </td>
                    <td>{item.scores?.technical ?? "—"}%</td>
                    <td>{item.scores?.aptitude  ?? "—"}%</td>
                    <td>{item.scores?.workstyle ?? "—"}%</td>
                    <td>
                      <span className="history-score">{item.scores?.total ?? "—"}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="history-empty">
            Complete an assessment test to see your scores here.
          </div>
        )}
      </section>

      {/* Coding Completion History */}
      <section className="history-section">
        <div className="history-section-header">
          <div>
            <p className="history-kicker" style={{ color: "rgba(78,205,196,0.9)" }}>Coding History</p>
            <h2>Coding practice completions</h2>
          </div>
          <span>{coding_history.length} submissions</span>
        </div>

        {coding_history.length > 0 ? (
          <div className="history-table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Problem</th>
                  <th>Date</th>
                  <th>Language</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {coding_history.map((item) => (
                  <tr key={item.submission_id}>
                    <td>
                      <div className="history-role">{item.problem_title || "Problem"}</div>
                    </td>
                    <td>{item.created_at || "N/A"}</td>
                    <td>
                      <span className="history-mode" style={{ textTransform: "capitalize" }}>
                        {item.language || "—"}
                      </span>
                    </td>
                    <td>
                      <span className={`history-mode${item.status?.toLowerCase() === "accepted" ? "" : ""}`}
                        style={{
                          color: item.status?.toLowerCase() === "accepted"
                            ? "rgba(78,205,196,0.9)"
                            : "rgba(255,255,255,0.55)",
                          textTransform: "capitalize",
                        }}>
                        {item.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="history-empty">
            Submit a coding problem solution to see your practice history here.
          </div>
        )}
      </section>

      {/* Trial Sessions */}
      {trial_sessions && trial_sessions.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ color: "#fff", marginBottom: "0.75rem" }}>🎯 Trial Interviews</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {trial_sessions.map((session) => (
              <div
                key={session.session_id}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "0.75rem",
                  padding: "1rem 1.25rem",
                  border: "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "0.75rem"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <span style={{
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    padding: "0.2rem 0.5rem",
                    borderRadius: "0.3rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}>
                    Trial
                  </span>
                  <div>
                    <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>
                      {session.role || "Interview"}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
                      {session.created_at}
                    </div>
                  </div>
                </div>

                {/* Preview Scores */}
                {session.preview_scores && (
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#667eea", fontWeight: 700, fontSize: "1.1rem" }}>
                        {session.preview_scores.communication || "—"}
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem" }}>Comm.</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#4ECDC4", fontWeight: 700, fontSize: "1.1rem" }}>
                        {session.preview_scores.confidence || "—"}
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem" }}>Conf.</div>
                    </div>
                  </div>
                )}

                {/* Locked Report */}
                <a
                  href="/pricing"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.85rem",
                    textDecoration: "none",
                    background: "rgba(255,255,255,0.05)",
                    padding: "0.4rem 0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255,255,255,0.1)",
                    transition: "border-color 0.15s"
                  }}
                >
                  🔒 Upgrade to unlock report
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {entitlement_status === "EXPIRED" && (
        <div className="cta-section expired">
          <h3>Subscription Expired</h3>
          <p>Renew your subscription to continue practicing</p>
          <a href="/payment" className="btn-primary">
            Renew Subscription
          </a>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
